import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Diamond, Save, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { exportToCsv } from "@/lib/csv";
import { ResellerAccessCard } from "@/components/admin/ResellerAccessCard";

export default function AdminResellerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [recharges, setRecharges] = useState([]);
  const [stockPrices, setStockPrices] = useState({ olaparty: "", trumeet: "" });
  const [savingSP, setSavingSP] = useState(false);
  const [rStart, setRStart] = useState("");
  const [rEnd, setREnd] = useState("");

  const rechargeInRange = (iso) => {
    const t = new Date(iso).getTime();
    if (rStart && t < new Date(rStart).getTime()) return false;
    if (rEnd && t > new Date(rEnd).getTime()) return false;
    return true;
  };
  const rechargeFiltered = recharges.filter((r) => rechargeInRange(r.created_at));
  const exportReseller = () => exportToCsv(`reseller-recharges-${data?.reseller?.name || id}`, rechargeFiltered, [
    { header: "Timestamp", accessor: (r) => new Date(r.created_at).toLocaleString() },
    { header: "Platform", accessor: (r) => r.platform_name || r.platform },
    { header: "Type", accessor: (r) => (r.type === "noble" ? `${r.tier_label || "Noble"}${r.mode === "renew" ? " renew" : ""}` : "Transfer") },
    { header: "Recipient UID", accessor: (r) => r.recipient_uid },
    { header: "Deducted", accessor: (r) => r.diamonds },
    { header: "Delivered", accessor: (r) => r.transferred ?? r.diamonds },
    { header: "Balance after", accessor: (r) => r.balance_after },
    { header: "Status", accessor: (r) => r.status },
    { header: "Note", accessor: (r) => r.note },
  ]);

  const refresh = async () => {
    const [d, a, r] = await Promise.all([
      api.get(`/admin/resellers/${id}`),
      api.get(`/admin/allocations`, { params: { reseller_id: id } }),
      api.get(`/admin/recharges`, { params: { reseller_id: id } }),
    ]);
    setData(d.data);
    setAllocations(a.data);
    setRecharges(r.data);
    const sp = d.data?.reseller?.stock_prices || {};
    setStockPrices({ olaparty: sp.olaparty ?? "", trumeet: sp.trumeet ?? "" });
  };

  useEffect(() => { refresh(); }, [id]);

  const saveStockPrices = async () => {
    setSavingSP(true);
    try {
      await api.patch(`/admin/resellers/${id}/stock-prices`, { prices: {
        olaparty: stockPrices.olaparty === "" ? 0 : parseFloat(stockPrices.olaparty),
        trumeet: stockPrices.trumeet === "" ? 0 : parseFloat(stockPrices.trumeet),
      } });
      toast.success("Stock prices saved for this reseller");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSavingSP(false); }
  };

  const handleStatus = async (status) => {
    try {
      await api.patch(`/admin/resellers/${id}`, { status });
      toast.success(`Reseller ${status}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!data) return <div className="text-zinc-500">Loading…</div>;
  const r = data.reseller;
  const successDiamonds = recharges
    .filter((x) => x.status === "success")
    .reduce((s, x) => s + (x.diamonds || 0), 0);

  return (
    <div className="space-y-6" data-testid="reseller-detail-page">
      <Link to="/admin/resellers" className="inline-flex items-center text-sm text-zinc-600 hover:text-zinc-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to resellers
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Profile</p>
          <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">{r.name || "—"}</h1>
          <p className="text-zinc-500 mt-1">
            {r.email} · {r.phone || "no phone"}
            {(r.city || r.state) && ` · ${[r.city, r.state].filter(Boolean).join(", ")}`}
          </p>
        </div>
        <div className="flex gap-2">
          {r.status === "active" ? (
            <Button variant="outline" onClick={() => handleStatus("disabled")} className="text-red-600 hover:text-red-700">Disable</Button>
          ) : (
            <Button onClick={() => handleStatus("active")} className="bg-green-600 hover:bg-green-700">Enable</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard label="Total recharges" value={recharges.length.toLocaleString()} />
        <SummaryCard label="Successful sales" value={recharges.filter((x) => x.status === "success").length.toLocaleString()} />
        <SummaryCard label="Diamonds sold" value={successDiamonds.toLocaleString()} />
      </div>

      <ResellerAccessCard reseller={r} onSaved={refresh} />

      <div className="space-y-3">
        {Object.entries(r.balances || {}).map(([key, b]) => (
          <div key={key} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`balance-card-${key}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-zinc-900">{b.name || key}</div>
              <Diamond className="w-4 h-4 text-zinc-400" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Stat label="Allocated" value={(b.allocated ?? 0).toLocaleString()} />
              <Stat label="Used" value={(b.used ?? 0).toLocaleString()} />
              <Stat label="Balance" value={(b.balance ?? 0).toLocaleString()} />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="reseller-stock-prices-card">
        <h3 className="font-display text-base font-medium text-zinc-900 mb-1">Custom stock prices</h3>
        <p className="text-sm text-zinc-500 mb-4">₹ per diamond this reseller pays when refilling stock. Leave blank to use the platform default.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Olaparty (₹/💎)</Label>
            <Input type="number" step="0.01" min="0" value={stockPrices.olaparty} onChange={(e) => setStockPrices((p) => ({ ...p, olaparty: e.target.value }))} placeholder="default" className="w-36" data-testid="reseller-stock-price-olaparty" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Trumeet (₹/💎)</Label>
            <Input type="number" step="0.01" min="0" value={stockPrices.trumeet} onChange={(e) => setStockPrices((p) => ({ ...p, trumeet: e.target.value }))} placeholder="default" className="w-36" data-testid="reseller-stock-price-trumeet" />
          </div>
          <Button onClick={saveStockPrices} disabled={savingSP} className="bg-zinc-900 hover:bg-zinc-800" data-testid="reseller-stock-price-save"><Save className="w-4 h-4 mr-1" /> {savingSP ? "Saving…" : "Save"}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Allocations" empty="No allocations yet">
          {allocations.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-b-0 text-sm">
              <div>
                <div className={`font-medium ${a.amount < 0 ? "text-amber-700" : "text-zinc-900"}`}>
                  {a.amount < 0 ? "−" : "+"}{Math.abs(a.amount).toLocaleString()} diamonds
                  <span className="text-xs text-zinc-500"> · {a.platform_name || "Trumeet"}{a.amount < 0 ? " · taken back" : ""}</span>
                </div>
                <div className="text-zinc-500 text-xs">{a.note || "—"} · {new Date(a.created_at).toLocaleString()}</div>
              </div>
              <div className="text-xs text-zinc-500 font-mono">{a.admin_email}</div>
            </div>
          ))}
        </Section>
        <Section title="Recharges" empty="No recharges yet"
          header={(
            <div className="flex flex-wrap items-center gap-2" data-testid="reseller-detail-recharge-controls">
              <Input type="datetime-local" value={rStart} max={rEnd || undefined} onChange={(e) => setRStart(e.target.value)} className="h-8 w-48 text-xs" data-testid="reseller-detail-start" />
              <span className="text-xs text-zinc-400">to</span>
              <Input type="datetime-local" value={rEnd} min={rStart || undefined} onChange={(e) => setREnd(e.target.value)} className="h-8 w-48 text-xs" data-testid="reseller-detail-end" />
              {(rStart || rEnd) && <button onClick={() => { setRStart(""); setREnd(""); }} className="text-xs text-zinc-500 hover:text-zinc-800">Clear</button>}
              <button onClick={exportReseller} data-testid="reseller-detail-export-csv" className="ml-auto inline-flex items-center gap-1 h-8 px-3 text-xs rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 font-medium"><Download className="w-3.5 h-3.5" /> Download CSV</button>
            </div>
          )}>
          {rechargeFiltered.length === 0 && <div className="py-6 text-center text-zinc-400 text-sm">No recharges in this range</div>}
          {rechargeFiltered.slice(0, 100).map((rr) => (
            <div key={rr.id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-b-0 text-sm">
              <div>
                <div className="text-zinc-900 font-medium font-mono">{rr.recipient_uid} <span className="text-xs text-zinc-500 font-sans">· {rr.platform_name || "Trumeet"}</span></div>
                <div className="text-zinc-500 text-xs">{new Date(rr.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-zinc-900 tabular-nums font-mono">{rr.diamonds}</div>
                <StatusPill status={rr.status} />
              </div>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-semibold tabular-nums font-mono mt-1 text-zinc-900">{value}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-display text-2xl font-semibold tabular-nums font-mono">{value}</div>
    </div>
  );
}

function Section({ title, empty, children, header }) {
  const hasChildren = Array.isArray(children) ? children.some(Boolean) : !!children;
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6">
      <h3 className="font-display text-base font-medium text-zinc-900 mb-3">{title}</h3>
      {header && <div className="mb-3">{header}</div>}
      {!hasChildren ? <div className="text-sm text-zinc-400">{empty}</div> : <div>{children}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    success: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
    pending: "bg-amber-100 text-amber-800",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}
